import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { QrCode, Print, QrCodeScanner } from '@mui/icons-material';
import { QREntity } from './components/QRGenerator';

import QRGenerator from './components/QRGenerator';
import LabelDesigner from './components/LabelDesigner';
import BatchPrint from './components/BatchPrint';
import QRScanner from './components/QRScanner';

const QRManager: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [printQueue, setPrintQueue] = useState<QREntity[]>([]);
  const [selectedPrintItems, setSelectedPrintItems] = useState<string[]>([]);

  const handleAddToPrintQueue = (entity: QREntity) => {
    setPrintQueue((prev) => {
      // Avoid duplicates
      if (prev.find((e) => e.id === entity.id)) return prev;
      return [...prev, entity];
    });
  };

  const handleTogglePrintItem = (id: string) => {
    setSelectedPrintItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPrintItems.length === printQueue.length) {
      setSelectedPrintItems([]);
    } else {
      setSelectedPrintItems(printQueue.map((e) => e.id));
    }
  };

  const handleClearQueue = () => {
    setPrintQueue([]);
    setSelectedPrintItems([]);
  };

  const handleRemoveFromQueue = (id: string) => {
    setPrintQueue((prev) => prev.filter((e) => e.id !== id));
    setSelectedPrintItems((prev) => prev.filter((i) => i !== id));
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            QR Code Manager
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            Generate, design, print, and scan QR codes for shop floor entities
          </Typography>
        </Box>
        {printQueue.length > 0 && (
          <Chip
            icon={<Print />}
            label={`${printQueue.length} in print queue`}
            color="primary"
            variant="outlined"
            onClick={() => setSelectedTab(2)}
            clickable
          />
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <QrCode color="primary" />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Entity Types
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    5
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Print color="warning" />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Print Queue
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {printQueue.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <QrCodeScanner color="success" />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Scan Mode
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    Desktop
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <QrCode color="info" />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Label Sizes
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    3
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<QrCode />} iconPosition="start" label="Generate QR Codes" />
          <Tab icon={<Print />} iconPosition="start" label="Label Designer" />
          <Tab
            icon={<Print />}
            iconPosition="start"
            label={
              printQueue.length > 0
                ? `Batch Print (${printQueue.length})`
                : 'Batch Print'
            }
          />
          <Tab icon={<QrCodeScanner />} iconPosition="start" label="Scan / Verify" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <QRGenerator onAddToPrintQueue={handleAddToPrintQueue} />
      )}

      {selectedTab === 1 && (
        <LabelDesigner />
      )}

      {selectedTab === 2 && (
        <BatchPrint
          printQueue={printQueue}
          selectedItems={selectedPrintItems}
          onToggleSelect={handleTogglePrintItem}
          onSelectAll={handleSelectAll}
          onClearQueue={handleClearQueue}
          onRemoveItem={handleRemoveFromQueue}
        />
      )}

      {selectedTab === 3 && (
        <QRScanner />
      )}
    </Box>
  );
};

export default QRManager;
